import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { ICellRendererParams } from 'ag-grid-community';
import { Button, InputGroup } from '@blueprintjs/core';
import { TranslatedMessage } from '../common/translation';
import { UserStore } from '../../store/UserStore';
import { AuthenticationStore, LocaleStore, SecretariatStore } from '../../store';
import { ModalStore } from '../../store/ModalStore';
import { Link } from 'react-router-dom';
import { Page, TLCode } from '../../common/enums';
import { Instance } from 'mobx-state-tree';
import { ApplicationStore } from '../../store';
import styled from '@emotion/styled';
import BaseGrid from '../common/grid/BaseGrid';
import MainSectionLayout from '../layout/MainSectionLayout';
import EditCell from '../common/grid/EditCell';

const ContainerCol = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const DivLeft = styled.div`
  display: flex;
  flex-direction: column;
  width: 30%;
  margin-right: 5%;
  padding-bottom: 5px;
`;
const DivRight = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 5px;
  .bp3-icon {
    margin-right: 3px;
  }
`;

interface UserListPageProps {
  UserStore?: Instance<typeof UserStore>;
  LocaleStore?: Instance<typeof LocaleStore>;
  ModalStore?: Instance<typeof ModalStore>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
}

interface UserListPageState {
  isLoading: boolean;
  filter: string;
  columnDefs: any;
  defaultColDef: any;
  api?: any;
  columnApi?: any;
  // users?: Array<Instance<typeof User>>;
}

// const Search = Input.Search;
export function renderStatus(params: ICellRendererParams) {
  const { value } = params;
  return value ? '' : 'no';
}

@inject('UserStore', 'LocaleStore', 'ModalStore', 'ApplicationStore', 'AuthenticationStore', 'SecretariatStore')
@observer
export default class UserListPage extends React.Component<UserListPageProps, UserListPageState> {
  deleteUser = async (id: number) => {
    const confirm = await this.props.ModalStore!.confirm.show('title', 'Are you sure you want to delete user?');
    if (confirm) {
      await this.props.UserStore!.deleteUsers([id]);
    }
  };

  constructor(props: {}) {
    super(props);
    this.state = {
      isLoading: false,
      filter: '',
      defaultColDef: {
        filter: false,
        sortable: true,
      },
      columnDefs: [
        {
          headerName: this.props.LocaleStore!.getText(TLCode.Lastname),
          width: 200,
          sort: 'asc',
          field: 'lastName',
        },
        {
          headerName: this.props.LocaleStore!.getText(TLCode.Firstname),
          width: 200,
          field: 'firstName',
          sortingOrder: ['asc', 'desc'],
        },
        {
          headerName: this.props.LocaleStore!.getText(TLCode.Username),
          width: 200,
          field: 'userName',
          sortingOrder: ['asc', 'desc'],
        },
        {
          headerName: this.props.LocaleStore!.getText(TLCode.Email),
          width: 200,
          field: 'email',
          sortingOrder: ['asc', 'desc'],
        },
        {
          headerName: this.props.LocaleStore!.getText(TLCode.IS_ACTIVE),
          field: 'status',
          width: 50,
          sortable: true,
          cellRendererFramework: renderStatus,
        },
        {
          headerName: this.props.LocaleStore!.getText(TLCode.Role),
          width: 200,
        },
        {
          headerName: '',
          width: 100,
          sortable: false,
          cellRendererFramework: EditCell,
          cellRendererParams: (params: ICellRendererParams) => {
            return {
              onEditButtonClick: () => {
                this.props.ApplicationStore!.navigate(`${Page.UserPage}/${params.data.id}`);
              },
            };
          },
        },
      ],
    };
  }

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    const { UserStore } = this.props;
    this._isMounted && this.setState({ isLoading: true });
    if (UserStore!.dataList.length == 0 || UserStore!.dataList[0].roles.length == 0) {
      await UserStore!.getWithRole();
    }
    this._isMounted && this.setState({ isLoading: false });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  showUser = (user: any) => {
    return (
      (this.props.AuthenticationStore!.isSuperAdmin && this.props.SecretariatStore!.selectedSecretariatId === 0) ||
      (this.props.AuthenticationStore!.isSuperAdmin &&
        user !== undefined &&
        this.props.SecretariatStore!.selectedSecretariatId !== undefined &&
        this.props.SecretariatStore!.selectedSecretariatId === user!.secretaryId) ||
      (user !== undefined &&
        this.props.AuthenticationStore!.isSecretariat &&
        this.props.AuthenticationStore!.secretariatId === user!.secretaryId)
    );
  };

  buildGridData = () => {
    return this.props
      .UserStore!.dataList.filter((u) => this.showUser(u))
      .map((user) => {
        return {
          id: user.userId,
          firstName: user.firstName,
          lastName: user.lastName,
          userName: user.userName,
          email: user.email,
          roles: user.roles,
          status: user.isActive,
        };
      });
  };

  setFilterText(filter) {
    this.setState({
      filter,
    });
  }

  private onRowDoubleClicked = (ev) => {
    this.props.ApplicationStore!.navigate(`${Page.UserPage}/${ev.data.id}`);
  };

  public render() {
    return (
      <MainSectionLayout isLoading={this.state.isLoading} title={TLCode.USERS_Title}>
        <ContainerCol>
          <DivLine>
            <DivLeft>
              <InputGroup
                placeholder="Filter"
                leftIcon="search"
                value={this.state.filter}
                onChange={(e) => this.setFilterText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.charCode == 13) {
                    this.props.ApplicationStore!.navigate(
                      `${Page.UserPage}/${
                        this.props.UserStore!.dataList[
                          this.props.UserStore!.dataList.findIndex((us) => us.userName === this.state.filter)
                        ].userId
                      }`,
                    );
                  }
                  return false;
                }}
              />
            </DivLeft>
            <DivRight>
              <Link to={Page.UserPage + '/-1'}>
                <Button intent="primary">
                  <TranslatedMessage id={TLCode.Add} />
                </Button>
              </Link>
            </DivRight>
          </DivLine>
          <BaseGrid
            keyName="id"
            defaultColDef={this.state.defaultColDef}
            columnDefs={this.state.columnDefs}
            rowData={this.buildGridData()}
            loading={this.state.isLoading}
            quickFilterText={this.state.filter}
            onRowDoubleClicked={this.onRowDoubleClicked}
          />
        </ContainerCol>
      </MainSectionLayout>
    );
  }
}
